// @ts-strict-ignore
import { requestApi } from 'api/api'
import { BeneficiaryEntityType } from 'types/beneficiaries'
import {
  Contact,
  ContactCase,
  ContactCaseCount,
  ContactEntityType,
  ContactInstrument,
  ContactInstrumentProps,
  ContactPayload,
  ContactPaymentInstrumentsResponse,
  Contacts,
  ContactsRequest,
  ContactsV3Request,
  ContactsV3Response,
  CreateContactBankAccountInstrumentProps,
  CreateContactBankAccountResponse,
  InstrumentType,
  PostContactCase,
  PutContactProps,
  RelationshipType,
} from 'types/contacts'
import { TwoFAAuthRequest } from 'types/generics'
import { Paging } from 'types/types'
import { API_PAGINATION_LIMIT, API_PAGINATION_OFFSET } from 'utils/pagination'

const getContactInstrumentsSearchQuery = (searchTerm: string) => {
  return searchTerm
    ? `${searchTerm} verificationStatuses:VERIFIED`
    : 'verificationStatuses:VERIFIED'
}

const DEFAULT_CONTACT_INSTRUMENTS_PROPS: GetContactInstrumentProps = {
  searchTerm: '',
  size: API_PAGINATION_LIMIT,
  page: API_PAGINATION_OFFSET,
  sort: '',
}

const DEFAULT_CONTACT_INSTRUMENTS_FOR_WALLET_PROPS: GetWalletContactInstrumentProps = {
  walletLsid: '',
  searchTerm: '',
  size: API_PAGINATION_LIMIT,
  page: API_PAGINATION_OFFSET,
}
interface GetContactInstrumentProps {
  searchTerm: string
  paymentMethod?: InstrumentType
  size?: number
  page?: number
  sort?: string
  currency?: string
}

interface GetWalletContactInstrumentProps extends GetContactInstrumentProps {
  walletLsid?: string
  relationshipType?: RelationshipType
  entityType?: BeneficiaryEntityType
  isVersion3?: boolean
}

interface GetSearchQueryProps {
  walletLsid?: string
  searchTerm: string
  paymentMethod?: InstrumentType
  currency?: string
  type?: ContactEntityType
  relationshipType?: RelationshipType
  entityType?: BeneficiaryEntityType
}

const getSearchQuery = ({
  walletLsid,
  searchTerm,
  paymentMethod,
  currency,
  type,
  relationshipType,
  entityType,
}: GetSearchQueryProps) => {
  const q = [
    ...(walletLsid ? [`walletId:"${walletLsid}"`] : []),
    ...(searchTerm ? [searchTerm] : []),
    ...(paymentMethod ? [`paymentMethods:${paymentMethod}`] : []),
    ...(currency ? [`currency:${currency}`] : []),
    ...(type || entityType ? [`type:${type || entityType}`] : []),
    ...(relationshipType ? [`relationshipTypes:${relationshipType}`] : []),
    ...(entityType ? [`entityTypes:${entityType}`] : []),
    'verificationStatuses:VERIFIED',
  ]
  return q?.join(' ')
}

const getContactsSearchQuery = (searchTerm: string, type: ContactEntityType) => {
  const searchParams = [...(searchTerm ? [searchTerm] : []), ...(type ? [`type:${type}`] : [])]

  if (!searchParams.length) return

  return searchParams.join(' ')
}

export const getContacts = ({
  size = API_PAGINATION_LIMIT,
  page = 0,
  searchQuery = '',
  contactIds = [],
  totalCountLimit,
  type,
}: ContactsRequest): Promise<Contacts> => {
  const searchQueryParam = getContactsSearchQuery(searchQuery, type)

  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v1/contacts',
    method: 'GET',
    params: {
      ...(size && { size }),
      ...(page && { page }),
      ...(searchQueryParam && { q: searchQueryParam }),
      ...(totalCountLimit && { totalCountLimit }),
      ...(contactIds && contactIds?.length > 0 && { ids: contactIds.join(',') }),
    },
  })
}

export const getContactsV3 = ({
  size = API_PAGINATION_LIMIT,
  page = 0,
  searchQuery = '',
}: ContactsV3Request): Promise<ContactsV3Response> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v3/contacts',
    method: 'GET',
    params: {
      ...(searchQuery && { q: searchQuery }),
      pageSize: size,
      pageNumber: page,
    },
  })
}

export const postContact = (data: ContactPayload): Promise<Contact> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v1/contacts',
    method: 'POST',
    data,
  })
}

export const putContact = ({ contactId, contact }: PutContactProps): Promise<Contact> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}`,
    method: 'PUT',
    data: contact,
  })
}

export const getContactCaseCount = (): Promise<ContactCaseCount> => {
  return requestApi({
    url: '/payments/v1/travel-rule-rfis/count',
    method: 'GET',
  })
}

export const getContactCases = async ({
  paging,
}: {
  paging: Paging
}): Promise<{ results: ContactCase[]; paging: Paging }> => {
  const { offset, max } = paging

  const { data, headers } = await requestApi({
    url: '/payments/v1/travel-rule-rfis',
    fullResponse: true,
    method: 'GET',
    params: {
      ...(max && { max }),
      offset,
    },
  })

  return {
    results: data,
    paging: {
      total: Number(headers['x-api-list-total']) || 0,
      offset: Number(headers['x-api-list-start']) || offset,
      max,
    },
  }
}

export const postContactCase = (data: PostContactCase): Promise<ContactCase> => {
  return requestApi({
    url: `/payments/v1/travel-rule-rfis/${data?.externalId}/assign-contact`,
    method: 'POST',
    data: { contactId: data?.contactId },
  })
}

export const postContactInstrument = ({
  contactId,
  instrument,
}: ContactInstrumentProps): Promise<ContactInstrument> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}/instruments`,
    method: 'POST',
    data: instrument,
  })
}

export const putContactInstrument = ({
  instrumentId,
  contactId,
  instrument,
}: ContactInstrumentProps): Promise<ContactInstrument> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}/instruments/${instrumentId}`,
    method: 'PUT',
    data: instrument,
  })
}

export const getPaymentInstruments = ({
  searchTerm,
  size = API_PAGINATION_LIMIT,
  page = API_PAGINATION_OFFSET,
  sort = '',
  paymentMethod,
  currency,
}: GetContactInstrumentProps = DEFAULT_CONTACT_INSTRUMENTS_PROPS): Promise<ContactPaymentInstrumentsResponse> => {
  // Build the 'q' parameter based on feature flag and searchTerm
  const q = getSearchQuery({ searchTerm, paymentMethod, currency })

  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/platform/v1/payment-instruments',
    method: 'GET',
    params: {
      size,
      page,
      ...(sort && { sort }),
      ...(q && { q }),
    },
  })
}

export const postCreateContactBankAccount = ({
  contactId,
  request,
}: CreateContactBankAccountInstrumentProps): Promise<CreateContactBankAccountResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/platform/v1/contacts/${contactId}/instruments`,
    method: 'POST',
    data: request,
  })
}

export const postCreateContactBankAccountTwoFactorAuth = ({
  callbackUrl,
  challenge,
  tokenResponse,
}: TwoFAAuthRequest): Promise<CreateContactBankAccountResponse> => {
  return requestApi({
    baseUrl: callbackUrl,
    url: '',
    method: 'POST',
    data: {
      challenge,
      tokenResponse,
    },
  })
}

export const getWalletContactInstruments = ({
  walletLsid,
  searchTerm,
  size = API_PAGINATION_LIMIT,
  page = API_PAGINATION_OFFSET,
  sort = '',
  paymentMethod,
  currency,
  relationshipType,
  entityType,
  isVersion3 = false,
}: GetWalletContactInstrumentProps = DEFAULT_CONTACT_INSTRUMENTS_FOR_WALLET_PROPS): Promise<ContactPaymentInstrumentsResponse> => {
  const q = getSearchQuery({
    walletLsid,
    searchTerm,
    paymentMethod,
    currency,
    relationshipType,
    entityType,
  })

  const url = isVersion3 ? '/platform/v3/payment-instruments' : '/platform/v2/payment-instruments'

  return requestApi({
    baseUrl: process.env.API_URL,
    url,
    method: 'GET',
    params: {
      size,
      page,
      ...(sort && { sort }),
      ...(q && { q }),
    },
  })
}
